// Estado e regras do formulário de report dos tipos PADRÃO — Entrevista, Parceria, Negociação e as etapas do
// recrutamento de PN — no fluxo novo (`report_version = 1`, as perguntas de sempre da antiga AppointmentReportSidebar).
//
// Usado pelo `StandardReportForm.vue` (página do Report e painel da central). Tudo o que o mixin cria tem o prefixo `sr`
// (dados em `srReport`, computeds/métodos `sr*`) para não colidir com quem o usa. O Momento X continua no
// `momentXReportForm` (formulário do CX, `report_version = 2`); os dois têm o mesmo ciclo de vida e os mesmos estados.
//
// ── Uso ─────────────────────────────────────────────────────────────────────────────────────────────────────────
//   mixins: [standardReportForm],
//   created() { this.srInit(this.appointmentId, { appointment }) },  // `appointment` opcional (ver `srInit`)
//   Painel da central (linhas baixas, select até 600px, "Finalizando…" ≥ 700ms, barra `pane`): `srCompact: true`.
//
// ── Regras (as mesmas do `FinalizeStandardReportRequest`; ver constants/appointment_report_standard) ─────────────
//   "Houve reunião?" obrigatória · Sim → "Fechou negócio?" (ou "Foi aprovado?" no recrutamento de PN) · Não, não
//   fechou ou não aprovado → "Qual o motivo?" · Não houve → "Deseja remarcar a reunião?" (Sim → datas, formato e
//   link/endereço, sem conflito de horário) · "Comentários do report" opcional · especialista: ver
//   `standardReportContext`. Como na sidebar, trocar "Houve reunião?" limpa o resultado, o motivo e o remarcar, e
//   trocar o resultado limpa o motivo (as listas de motivo mudam com o caminho).
//
// ── Estados (`srReport.status`, ver SR_REPORT_STATUS — os mesmos valores do Momento X) ─────────────────────────
//   loading → editing → finalizing → saved
//             editing ⇄ error (rede/5xx: respostas mantidas, "Tentar novamente")
//   already_reported (409 / report existente: somente leitura) · unavailable (422 NOT_OWNER / NOT_REPORTABLE, ou
//   compromisso fora de "Agendado" ao abrir) · load_error.
//
// ── Antes do horário (`srReport.early`) ──────────────────────────────────────────────────────────────────────
//   O formulário já abre em `editing`, com o aviso de horário no topo. "Houve reunião? Não" libera o caminho inteiro
//   do "não houve reunião" (motivo, remarcar, comentários, especialista) e finaliza na hora — é o cancelamento
//   antecipado de sempre, que o servidor aceita (`AppointmentService::standardReportOpensBeforeStart`). Sem resposta
//   ou com "Sim", o resto aparece com cadeado e a finalização espera o horário (o servidor responde 423 NOT_OPEN).
//   No horário, o banner emite `released` e tudo libera. O Momento X não tem isso: lá o formulário inteiro espera o
//   horário (`waiting`, Figma) — este formulário nunca usa `waiting`.
//
// ── Eventos (emitidos pelo componente que usa o mixin) ─────────────────────────────────────────────────────────
//   loaded            { appointmentId, appointment, report, status }
//   reported          { appointmentId, eventType, report, replayed, pendingCount, closedDeal, hasApproval,
//                       offersContract, offersIndividualInterview, rescheduled, reschedule, answers, context }
//                     (`reschedule` = { status: 'error', message, payload } quando o reagendamento falhou, senão null)
//   already-reported  report (resumo `presentReport`) | null
//   pending-rejected  { appointmentId, pendingIds } — 423 da trava para um compromisso que saiu das pendências
//   draft-saved       { appointmentId, savedAt }
//   rescheduled       { appointmentId, response } — o `POST /api/appointments` do "Deseja remarcar a reunião?"
//   post-save-dismissed { appointmentId, kind } — "Reportar depois" / "Responder depois"
//
// A trava (`account/reportLock`) é atualizada pela action FINALIZE_STANDARD_REPORT (a mesma lógica do Momento X):
// libera o compromisso localmente e dispara `REFRESH_REPORT_LOCK({ force: true })` em segundo plano.
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountTypes from '@/modules/account/store/types'
import * as appointmentTypes from '@/modules/appointment/store/types'
import { stringToDateTime } from '@/helpers/converters'
import { newIdempotencyKey } from '@/helpers/idempotency'
import {
  apiErrorToast,
  getApiErrorMessage,
  hasApiErrorCode,
  isReportNotOpenError,
  isReportPendingError,
} from '@/helpers/error_messages'
import {
  APP_COPY,
  FIGMA_COPY,
  NEW_COPY,
  REPORT_ERROR_CODES,
} from '@/constants/appointment_report_v2'
import {
  CONTRACT_RESOURCE,
  CREATE_ACTION,
  PLANEJADORES_SELECTION_MENU,
  READ_ACTION,
} from '@/constants/resources'
import {
  IN_PERSON_FORMAT,
  MAX_QUALITATIVE_HIGHLIGHTS,
  STANDARD_COPY,
  STANDARD_FIELDS as F,
  STANDARD_FORM_VERSION,
  STANDARD_REPORTABLE_STATUSES,
  answersFromStandardReport,
  appointmentSpecialistId,
  asksReschedule,
  asksWhy,
  buildReschedulePayload,
  buildStandardFinalizePayload,
  defaultMeetingEnd,
  emptyStandardAnswers,
  hasValidRescheduleRange,
  isWhyValid,
  ratingOnPath,
  reschedules,
  resultField,
  sanitizeStandardAnswers,
  standardQuestionLabel,
  standardReportContext,
  standardReportItems,
  standardReportSummaryLines,
  whyOptionsFor,
} from '@/constants/appointment_report_standard'
import { nextReportUid, prefersReducedMotion, toTimestamp } from '../helpers/report_ui'

export const SR_REPORT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOAD_ERROR: 'load_error',
  // Só do Momento X (mesmo vocabulário); aqui o "antes do horário" é `editing` + `early`.
  WAITING: 'waiting',
  EDITING: 'editing',
  FINALIZING: 'finalizing',
  ERROR: 'error',
  SAVED: 'saved',
  ALREADY_REPORTED: 'already_reported',
  UNAVAILABLE: 'unavailable',
}

// Próximo passo oferecido depois de salvar (as duas telas da sidebar).
export const SR_POST_SAVE = {
  CONTRACT: 'contract',
  INDIVIDUAL_INTERVIEW: 'individual_interview',
}

// Autosave do rascunho: 800ms depois da última resposta (o mesmo do Momento X).
const DRAFT_SAVE_DEBOUNCE_MS = 800
// "Finalizando…" visível no mínimo 900ms na página e 700ms no painel da central (boards v4) — um sucesso
// instantâneo parece falha.
const FINALIZE_MIN_BUSY_MS = 900
const FINALIZE_MIN_BUSY_MS_COMPACT = 700
// Autosave que falhou (rede/5xx): nova tentativa com espera crescente, até 1 min.
const DRAFT_RETRY_BASE_MS = 5000
const DRAFT_RETRY_MAX_MS = 60000
// Checagem de horário ocupado do reagendamento: 200ms depois de mudar a data (o `checkOverlapping` da sidebar).
const OVERLAP_CHECK_DELAY_MS = 200
// Formulário liberado no horário: os controles entram (30ms de intervalo).
const RELEASE_FX_MS = 450
const ENTER_STAGGER_MS = 30
// Linhas que surgem quando "Houve reunião?" é respondida (resultado ou remarcar).
const ROWS_FX_MS = 360

// Rotas dos próximos passos da sidebar (`onContractReport` / `openIndividualInterview`).
const CONTRACT_SAVE_ROUTE = 'contract-save'
const INDIVIDUAL_INTERVIEW_ROUTE = 'individual-interview-report'

// `POST /api/appointments/overlapping_appointments_check` responde 405 quando o horário está ocupado.
const OVERLAP_STATUS = 405

const UNAVAILABLE_CODES = [
  REPORT_ERROR_CODES.NOT_OWNER,
  REPORT_ERROR_CODES.WRONG_TYPE,
  REPORT_ERROR_CODES.NOT_REPORTABLE,
]

const EDITABLE_STATUSES = [SR_REPORT_STATUS.EDITING, SR_REPORT_STATUS.ERROR]
const DONE_STATUSES = [SR_REPORT_STATUS.SAVED, SR_REPORT_STATUS.ALREADY_REPORTED]

// Campos do corpo do finalize que podem voltar no 422 (`qualitative_highlights.0` → `qualitative_highlights`).
const SERVER_FIELDS = [
  F.HAD_MEETING,
  F.CLOSED_DEAL,
  F.HAS_APPROVAL,
  F.WHY,
  F.FEEDBACK,
  F.HAD_SUPPORT,
  F.SPECIALIST_ID,
  F.SPECIALIST_SCORE,
  F.HIGHLIGHTS,
]

// Os campos do reagendamento aparecem em grupo; o "ir para o que falta" leva a cada um deles pelo `ref`.
const RESCHEDULE_FIELDS = [F.MEETING_START, F.MEETING_END, F.MEETING_FORMAT, F.ROOM_LINK]
const SPECIALIST_FIELDS = [F.SPECIALIST_ID, F.SPECIALIST_SCORE, F.HIGHLIGHTS]

const wait = ms => new Promise(resolve => {
  setTimeout(resolve, Math.max(0, ms))
})

const responseData = error => (error && error.response && error.response.data) || {}
const responseStatus = error => error && error.response && error.response.status

// `POST /api/appointments` responde 405 quando uma regra do agendamento recusa o reagendamento (horário ocupado, etapa
// do candidato…).
const APPOINTMENT_RULE_STATUS = 405

/**
 * Texto da falha do "Deseja remarcar a reunião?": no 405 a mensagem do servidor, como na sidebar; no resto, o texto
 * genérico dela. Exportado para a central de pendências, que oferece o "Tentar novamente" do reagendamento na própria
 * confirmação depois que o formulário compacto sai da tela.
 */
export const rescheduleErrorMessage = error => (responseStatus(error) === APPOINTMENT_RULE_STATUS
  ? getApiErrorMessage(error, STANDARD_COPY.rescheduleError)
  : STANDARD_COPY.rescheduleError)

const localClock = () => {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => item === b[index])
  }
  return a === b
}

const initialReschedule = () => ({
  // 'idle' | 'saving' | 'done' | 'error'
  status: 'idle',
  message: '',
})

const initialState = () => ({
  appointmentId: null,
  status: SR_REPORT_STATUS.IDLE,
  answers: emptyStandardAnswers(),
  // Tentou finalizar com respostas faltando → as mensagens de obrigatório aparecem.
  attempted: false,
  // Campo alterado/saído pelo PN: libera as mensagens de valor inválido (URL, término antes do início) antes da
  // primeira tentativa de finalizar.
  touched: {},
  // Mensagens do 422 do servidor por campo.
  serverErrors: {},
  appointment: null,
  // Quem reporta é o especialista do cliente (`specialist === 1` do GET do compromisso): sem avaliação.
  isCustomerSpecialist: false,
  // Antes do horário de início (ver "Antes do horário" no topo). Volta a `false` quando o banner libera.
  early: false,
  opensAt: null,
  serverNow: null,
  serverNowReceivedAt: null,
  draftVersion: null,
  draftSavedAt: null,
  draftDirty: false,
  draftSaveFailed: false,
  // O servidor recusou o rascunho deste compromisso (422): o formulário segue sem autosave e quem decide é o finalize.
  draftDisabled: false,
  report: null,
  replayed: false,
  pendingCount: null,
  errorMessage: '',
  unavailableCode: null,
  unlocked: false,
  releasedFx: false,
  rowsFx: false,
  announce: '',
  // "Agora" da regra do reagendamento num horário que já passou (atualizado a cada resposta e ao finalizar).
  nowTick: Date.now(),
  // Reagendamento: horário ocupado (405 da checagem) e checagem em andamento.
  overlapping: false,
  overlapChecking: false,
  // Destaques do especialista para a nota escolhida (nomes de `GET appointments/qualitative_highlights/{nota}`).
  highlightOptions: [],
  highlightsLoading: false,
  reschedule: initialReschedule(),
  // "Reportar depois" / "Responder depois" já escolhido.
  postSaveDismissed: false,
})

export default {
  data() {
    return {
      srUid: nextReportUid('sr'),
      srReport: initialState(),
      // Layout do painel da central (o componente sobrescreve no próprio `data()` ou por prop).
      srCompact: false,
    }
  },
  computed: {
    srStatus() {
      return this.srReport.status
    },
    srIsLoading() {
      return this.srReport.status === SR_REPORT_STATUS.LOADING
    },
    // Antes do horário com o formulário na tela (ver "Antes do horário" no topo).
    srIsEarly() {
      return this.srReport.early && (this.srIsEditable || this.srIsFinalizing)
    },
    // Antes do horário, o caminho do "houve reunião" (e o resto do formulário) fica em prévia com cadeado até a
    // resposta ser "Não".
    srHeldLocked() {
      return this.srIsEarly && this.srReport.answers[F.HAD_MEETING] !== false
    },
    // "Houve reunião? Sim" antes do horário: nada falta responder agora, mas a finalização espera o horário.
    srFinalizeLocked() {
      return this.srIsEarly && this.srReport.answers[F.HAD_MEETING] === true
    },
    srIsEditable() {
      return EDITABLE_STATUSES.includes(this.srReport.status)
    },
    srIsFinalizing() {
      return this.srReport.status === SR_REPORT_STATUS.FINALIZING
    },
    srHasError() {
      return this.srReport.status === SR_REPORT_STATUS.ERROR
    },
    srIsDone() {
      return DONE_STATUSES.includes(this.srReport.status)
    },
    srIsSaved() {
      return this.srReport.status === SR_REPORT_STATUS.SAVED
    },
    srContext() {
      return standardReportContext(this.srReport.appointment, {
        isCustomerSpecialist: this.srReport.isCustomerSpecialist,
      })
    },
    // Com o caminho em prévia (antes do horário), só "Houve reunião?" conta: o contador da barra e o "ir para o que
    // falta" nunca apontam para uma pergunta com cadeado.
    srItems() {
      const items = standardReportItems(this.srReport.answers, this.srContext, {
        overlapping: this.srReport.overlapping,
        now: this.srReport.nowTick,
      })
      return this.srHeldLocked ? items.filter(item => item.key === F.HAD_MEETING) : items
    },
    srMissingItems() {
      return this.srItems.filter(item => !item.answered)
    },
    srMissingCount() {
      return this.srMissingItems.length
    },
    srIsComplete() {
      return this.srMissingCount === 0
    },
    srFinalizePayload() {
      return buildStandardFinalizePayload(this.srReport.answers, this.srContext)
    },
    srResultKey() {
      return resultField(this.srContext)
    },
    // "Fechou negócio?" / "Foi aprovado?" só depois de "Houve reunião? Sim" (como na sidebar). Antes do horário:
    // aparece na prévia (com cadeado), para o PN ver o que vai responder.
    srShowResult() {
      return this.srHeldLocked || this.srReport.answers[F.HAD_MEETING] === true
    },
    srShowRescheduleQuestion() {
      return asksReschedule(this.srReport.answers)
    },
    srReschedules() {
      return reschedules(this.srReport.answers)
    },
    srWhyOptions() {
      return whyOptionsFor(this.srReport.answers, this.srContext)
    },
    srShowSpecialistBlock() {
      return this.srContext.specialistBlock
    },
    srShowRating() {
      return ratingOnPath(this.srReport.answers, this.srContext)
    },
    srInPerson() {
      return this.srReport.answers[F.MEETING_FORMAT] === IN_PERSON_FORMAT
    },
    srRoomLinkLabel() {
      return this.srInPerson ? STANDARD_COPY.meetingAddress : STANDARD_COPY.meetingLink
    },
    // Especialistas (`GET interviews/proposal/consultants`, a mesma lista da sidebar). O select usa texto no valor:
    // os ids viram string aqui e voltam a número em `srSetAnswer`. O especialista do compromisso (e o escolhido no
    // rascunho) entram na lista mesmo que ela não o traga — senão o select mostraria o placeholder com valor escolhido.
    srSpecialistOptions() {
      const list = this.$store.getters[appointmentTypes.CONSULTANTS_SPECIALIST]
      const options = (Array.isArray(list) ? list : [])
        .filter(item => item && item.id)
        .map(item => ({ value: String(item.id), label: String(item.name || '') }))
      const ensure = (id, name) => {
        if (!id || options.some(option => option.value === String(id))) return
        options.unshift({ value: String(id), label: name || String(id) })
      }
      const { appointment, answers } = this.srReport
      const specialist = appointment && appointment.consultant_specialist
      ensure(appointmentSpecialistId(appointment), specialist && specialist.name)
      ensure(answers[F.SPECIALIST_ID], '')

      return options
    },
    // Formatos (`GET appointments/meeting_format_types`). Sem a lista (falhou), os dois do backend.
    srMeetingFormatOptions() {
      const list = this.$store.getters[appointmentTypes.APPOINTMENT_MEETING_FORMAT_TYPES]
      const options = (Array.isArray(list) ? list : [])
        .filter(item => item && item.id)
        .map(item => ({ value: String(item.id), label: String(item.description || item.id) }))
      if (options.length) return options

      return [
        { value: 'online', label: NEW_COPY.online },
        { value: IN_PERSON_FORMAT, label: NEW_COPY.inPerson },
      ]
    },
    srShowActionBar() {
      return this.srIsEditable || this.srIsFinalizing
    },
    srShowDateLock() {
      if (this.srIsEarly) return true
      return this.srReport.unlocked && (this.srIsEditable || this.srIsFinalizing)
    },
    srFinalizeMinBusyMs() {
      return this.srCompact ? FINALIZE_MIN_BUSY_MS_COMPACT : FINALIZE_MIN_BUSY_MS
    },
    // Antes do horário o report já está em andamento (o "não houve reunião" finaliza): a etapa fica ativa.
    srStepperState() {
      return this.srIsDone ? 'done' : 'active'
    },
    srMeetingUrl() {
      const { appointment } = this.srReport
      return appointment && appointment.meeting_format !== IN_PERSON_FORMAT ? (appointment.meeting_room_link || '') : ''
    },
    srDateLockProps() {
      return {
        opensAt: this.srReport.opensAt || '',
        serverNow: this.srReport.serverNow || '',
        serverNowReceivedAt: this.srReport.serverNowReceivedAt,
        meetingUrl: this.srMeetingUrl,
      }
    },
    srActionBarProps() {
      return {
        missingCount: this.srMissingCount,
        busy: this.srIsFinalizing,
        error: this.srHasError,
        errorMessage: this.srReport.errorMessage,
        savedAt: this.srReport.draftDisabled ? '' : (this.srReport.draftSavedAt || ''),
        ctaLabel: NEW_COPY.finalizeLegacyButton,
        lockedText: this.srFinalizeLocked ? NEW_COPY.reportAvailableAtMeeting : '',
        variant: this.srCompact ? 'pane' : 'page',
        entering: this.srReport.releasedFx,
      }
    },

    // ---- Depois de salvar ----
    // Respostas que valem para o resumo e os próximos passos: as do servidor, salvo quando o report acabou de ser
    // gravado por ESTE formulário (as locais trazem os destaques, que o resumo do servidor não traz). Num replay, o
    // report gravado pode ser de uma tentativa anterior: vale o do servidor.
    srSavedAnswers() {
      const { report, answers, replayed } = this.srReport
      if (this.srIsSaved && (!report || !replayed)) return answers
      return report ? answersFromStandardReport(report) : answers
    },
    srClosedDeal() {
      const values = this.srSavedAnswers
      return !this.srContext.isPn && values[F.HAD_MEETING] === true && values[F.CLOSED_DEAL] === true
    },
    srHasApproval() {
      const values = this.srSavedAnswers
      return this.srContext.isPn && values[F.HAD_MEETING] === true && values[F.HAS_APPROVAL] === true
    },
    srCanCreateContract() {
      return typeof this.$can !== 'function' || this.$can(CREATE_ACTION, CONTRACT_RESOURCE)
    },
    srCanOpenIndividualInterview() {
      return typeof this.$can !== 'function' || this.$can(READ_ACTION, PLANEJADORES_SELECTION_MENU)
    },
    // Ainda há pendências E a trava segura as outras telas: o `contract-save` e o questionário não abrem agora. Só a
    // contagem não basta: com a trava em `log`/`off` (`report_pendencies.blocked = false`) nada está bloqueado, e os
    // próximos passos da sidebar voltam a ser oferecidos como sempre foram. A action do finalize já gravou a trava com
    // a contagem nova antes de a resposta chegar aqui (`locked = locked && pending_count > 0`).
    srPendingRemain() {
      const locked = !!this.$store.getters[accountTypes.IS_REPORT_LOCKED]
      const { pendingCount } = this.srReport
      if (Number.isInteger(pendingCount)) return pendingCount > 0 && locked
      return locked
    },
    // Os próximos passos da sidebar — só logo depois de salvar aqui (como lá) e sem pendências restantes.
    srOffersContract() {
      return this.srContext.isPartnership && this.srClosedDeal
    },
    srOffersIndividualInterview() {
      return this.srContext.isIndividualInterview && this.srHasApproval
    },
    srPostSave() {
      if (!this.srIsSaved || this.srReport.postSaveDismissed || this.srPendingRemain) return ''
      if (this.srOffersContract && this.srCanCreateContract) return SR_POST_SAVE.CONTRACT
      if (this.srOffersIndividualInterview && this.srCanOpenIndividualInterview) return SR_POST_SAVE.INDIVIDUAL_INTERVIEW
      return ''
    },
    // Parceria fechada com pendências restantes: o cadastro do contrato fica para depois (como no Momento X).
    srContractDeferred() {
      return this.srIsSaved && this.srOffersContract && this.srCanCreateContract && this.srPendingRemain
    },
    srSummaryLines() {
      return standardReportSummaryLines(this.srSavedAnswers, this.srContext, {
        specialistName: id => this.srSpecialistName(id),
      })
    },
  },
  watch: {
    // O reagendamento abriu (ou voltou do rascunho): carrega os formatos e confere o horário.
    srReschedules(open) {
      if (!open) {
        this.srCancelOverlapCheck()
        return
      }
      this.srEnsureMeetingFormats()
    },
    srShowSpecialistBlock(show) {
      if (show) this.srEnsureSpecialists()
    },
  },
  created() {
    this.$_srDraftTimer = null
    this.$_srDraftRetryTimer = null
    this.$_srDraftRetryDelay = DRAFT_RETRY_BASE_MS
    this.$_srDraftRequest = null
    this.$_srDraftQueued = false
    this.$_srFinalizing = false
    this.$_srIdempotencyKey = null
    this.$_srLoadSeq = 0
    this.$_srReleaseTimer = null
    this.$_srRowsFxTimer = null
    this.$_srOverlapTimer = null
    this.$_srOverlapSeq = 0
    this.$_srOverlapRequest = null
    this.$_srHighlightsSeq = 0
    this.$_srHighlightsCache = {}
    this.$_srRescheduleDone = false
    this.$_srSpecialistsRequest = null
    this.$_srFormatsRequest = null
    this.$_srFlagRequestedFor = null
    this.$_srHint = null
    this.$_srDestroyed = false
    this.$_srOnBeforeUnload = () => {
      if (this.srReport.draftDirty && this.srIsEditable) this.srSaveDraft()
    }
    window.addEventListener('beforeunload', this.$_srOnBeforeUnload)
  },
  beforeDestroy() {
    // Sai da tela com resposta ainda não salva → salva agora (sem esperar o debounce nem o PUT em andamento).
    this.srFlushDraft()
    this.$_srDestroyed = true
    this.srClearTimers()
    this.srCancelOverlapCheck()
    window.removeEventListener('beforeunload', this.$_srOnBeforeUnload)
  },
  methods: {
    // ---------------------------------------------------------------------------------------------------------
    // Carregamento
    // ---------------------------------------------------------------------------------------------------------

    /**
     * Começa (ou recomeça) o formulário para o compromisso `appointmentId`. `options.appointment` = o que a tela já
     * tem do compromisso (linha da central, GET do compromisso…): só é lido o `specialist` do GET do compromisso
     * (quem reporta é o especialista do cliente → sem avaliação); o resto vem sempre do GET do rascunho.
     * `options.preloaded` = a resposta do GET do rascunho DESTE compromisso que a tela já buscou (a página do Report
     * lê o tipo do compromisso nela antes de escolher o formulário): aplicada direto, sem pedir de novo — senão a tela
     * piscaria dois carregamentos seguidos. "Tentar novamente" e os recarregamentos seguintes buscam normalmente.
     */
    srInit(appointmentId, options) {
      // Trocou de compromisso com resposta ainda não salva: salva a do anterior.
      this.srFlushDraft()
      this.srClearTimers()
      this.srCancelOverlapCheck()
      this.$_srDraftRequest = null
      this.$_srDraftQueued = false
      this.$_srFinalizing = false
      this.$_srRescheduleDone = false
      this.$_srFlagRequestedFor = null
      this.$_srHighlightsSeq += 1
      // Uma chave por rascunho: todas as tentativas de finalizar ESTE formulário reusam a mesma, e o servidor devolve
      // o report já gravado (`replayed`) em vez de criar outro — duplo clique, timeout, "Tentar novamente".
      this.$_srIdempotencyKey = newIdempotencyKey()
      this.$_srHint = (options && options.appointment) || null
      this.srReport = { ...initialState(), appointmentId: appointmentId ? Number(appointmentId) : null }
      const hint = this.$_srHint
      if (hint && Object.prototype.hasOwnProperty.call(hint, 'specialist')) {
        this.srReport.isCustomerSpecialist = Number(hint.specialist) === 1
      }

      const preloaded = options && options.preloaded
      const preloadedId = preloaded && preloaded.appointment && Number(preloaded.appointment.id)
      // Só vale a resposta do MESMO compromisso (a tela pode ter trocado de id no meio do caminho).
      if (preloaded && typeof preloaded === 'object' && preloadedId === this.srReport.appointmentId) {
        // Invalida um GET antigo que ainda esteja voltando (mesma regra do `srLoad`).
        this.$_srLoadSeq += 1
        this.srApplyShowResponse(preloaded)
        return Promise.resolve(preloaded)
      }

      return this.srLoad()
    },

    srLoad() {
      const id = this.srReport.appointmentId
      if (!id) return Promise.resolve(null)
      this.$_srLoadSeq += 1
      const sequence = this.$_srLoadSeq
      this.srReport.status = SR_REPORT_STATUS.LOADING
      this.srReport.errorMessage = ''

      return this.$store.dispatch(appointmentTypes.GET_REPORT_DRAFT, id)
        .then(resp => {
          if (sequence !== this.$_srLoadSeq || this.$_srDestroyed) return null
          if (!resp || !resp.data) throw new Error('Resposta vazia ao carregar o report.')
          this.srApplyShowResponse(resp.data)
          return resp.data
        })
        .catch(error => {
          if (sequence !== this.$_srLoadSeq || this.$_srDestroyed) return null
          this.srHandleLoadError(error)
          return null
        })
    },

    /** Resposta de GET appointments/{id}/report/draft → estado da tela. */
    srApplyShowResponse(data) {
      const body = data || {}
      const { appointment } = body
      this.srReport.appointment = appointment || null
      this.srReport.opensAt = body.report_opens_at || (appointment && appointment.appointment_start) || null
      this.srReport.serverNow = body.server_now || null
      this.srReport.serverNowReceivedAt = Date.now()

      if (body.report) {
        this.srApplyExistingReport(body.report)
        this.srEmitLoaded()
        return
      }

      // Guardas ao abrir (o servidor recusaria o rascunho e o finalize): Momento X é o outro formulário; fora de
      // "Agendado" o compromisso saiu do funil sem report (cancelado, excluído, remarcado).
      if (this.srContext.isMomentX) {
        this.srSetUnavailable(REPORT_ERROR_CODES.WRONG_TYPE, '')
        this.srEmitLoaded()
        return
      }
      const statusId = Number(appointment && appointment.status_id)
      if (statusId > 0 && !STANDARD_REPORTABLE_STATUSES.includes(statusId)) {
        this.srSetUnavailable(REPORT_ERROR_CODES.NOT_REPORTABLE, APP_COPY.notReportable)
        this.srEmitLoaded()
        return
      }

      const { draft } = body
      this.srApplyDraft(draft)
      this.srReport.early = this.srIsBeforeOpening()
      this.srReport.status = SR_REPORT_STATUS.EDITING
      this.srLoadSideData()
      this.srEmitLoaded()
    },

    /**
     * Rascunho do servidor → respostas. Só reidrata o formulário desta versão (`form_version = 1`); a `version` vale
     * sempre (é ela que o próximo PUT precisa). Sem rascunho, o especialista do compromisso já vem escolhido (sidebar).
     */
    srApplyDraft(draft) {
      const formVersion = draft && draft.form_version !== undefined && draft.form_version !== null
        ? Number(draft.form_version)
        : STANDARD_FORM_VERSION
      const restore = !!draft && formVersion === STANDARD_FORM_VERSION
      const answers = restore ? sanitizeStandardAnswers(draft.answers) : emptyStandardAnswers()
      if (!restore) answers[F.SPECIALIST_ID] = appointmentSpecialistId(this.srReport.appointment)
      // Motivo de outra lista (o caminho mudou num rascunho antigo): volta vazio.
      if (asksWhy(answers, this.srContext) && !isWhyValid(answers, this.srContext)) answers[F.WHY] = null

      this.srReport.answers = answers
      this.srReport.draftVersion = draft && Number.isInteger(draft.version) ? draft.version : null
      this.srReport.draftSavedAt = (restore && draft.updated_at) || null
      this.srReport.draftDirty = false
    },

    srApplyExistingReport(report) {
      this.srClearTimers()
      this.srCancelOverlapCheck()
      this.srReport.report = report
      this.srReport.draftDirty = false
      this.srReport.status = SR_REPORT_STATUS.ALREADY_REPORTED
      // O resumo mostra o nome do especialista avaliado.
      if (report && report.consultant_specialist_id) this.srEnsureSpecialists()
      this.srCallHook('onStandardReportAlreadyReported', report)
      this.$emit('already-reported', report || null)
    },

    srHandleLoadError(error) {
      if (this.srApplyUnavailable(error)) return
      if (isReportPendingError(error) && this.srApplyPendingRejection(error)) return
      this.srReport.status = SR_REPORT_STATUS.LOAD_ERROR
      this.srReport.errorMessage = isReportPendingError(error) ? '' : getApiErrorMessage(error, '')
    },

    srEmitLoaded() {
      this.$emit('loaded', {
        appointmentId: this.srReport.appointmentId,
        appointment: this.srReport.appointment,
        report: this.srReport.report,
        status: this.srReport.status,
      })
    },

    /** Listas e dados de apoio do formulário, conforme o caminho (cada um só uma vez). */
    srLoadSideData() {
      if (this.srShowSpecialistBlock) {
        this.srEnsureSpecialists()
        this.srLoadCustomerSpecialistFlag()
      }
      if (this.srReschedules) {
        this.srEnsureMeetingFormats()
        this.srScheduleOverlapCheck()
      }
      const score = this.srReport.answers[F.SPECIALIST_SCORE]
      if (score) this.srLoadHighlights(score, { keepSelection: true })
    },

    srEnsureSpecialists() {
      const list = this.$store.getters[appointmentTypes.CONSULTANTS_SPECIALIST]
      if ((Array.isArray(list) && list.length) || this.$_srSpecialistsRequest) return this.$_srSpecialistsRequest
      this.$_srSpecialistsRequest = this.$store.dispatch(appointmentTypes.GET_CONSULTANTS_SPECIALIST, this.srReport.appointmentId)
        .catch(error => {
          if (!isReportPendingError(error)) this.srToast({ text: STANDARD_COPY.loadSpecialistsError, variant: 'danger' })
        })
        .finally(() => {
          this.$_srSpecialistsRequest = null
        })

      return this.$_srSpecialistsRequest
    },

    srEnsureMeetingFormats() {
      const list = this.$store.getters[appointmentTypes.APPOINTMENT_MEETING_FORMAT_TYPES]
      if ((Array.isArray(list) && list.length) || this.$_srFormatsRequest) return this.$_srFormatsRequest
      this.$_srFormatsRequest = this.$store.dispatch(appointmentTypes.GET_APPOINTMENT_MEETING_FORMAT_TYPES)
        .catch(error => {
          if (!isReportPendingError(error)) this.srToast({ text: STANDARD_COPY.loadMeetingFormatsError, variant: 'danger' })
        })
        .finally(() => {
          this.$_srFormatsRequest = null
        })

      return this.$_srFormatsRequest
    },

    /**
     * `specialist` (quem reporta é o especialista do cliente) só vem no GET do compromisso — a API do rascunho não o
     * traz. Sem ele a sidebar escondia a avaliação; aqui ela aparece até a resposta chegar. Falhou → fica visível
     * (o servidor aceita os dois caminhos).
     */
    srLoadCustomerSpecialistFlag() {
      const hint = this.$_srHint
      if (hint && Object.prototype.hasOwnProperty.call(hint, 'specialist')) return
      const id = this.srReport.appointmentId
      // Uma consulta por compromisso (o conflito de rascunho recarrega os dados de apoio).
      if (!id || this.$_srFlagRequestedFor === id) return
      this.$_srFlagRequestedFor = id
      this.$store.dispatch(appointmentTypes.GET_APPOINTMENT, id)
        .then(resp => {
          if (this.$_srDestroyed || id !== this.srReport.appointmentId || !resp || !resp.data) return
          this.srReport.isCustomerSpecialist = Number(resp.data.specialist) === 1
        })
        .catch(() => {})
    },

    /** Antes do horário pelo relógio do servidor (server_now + tempo decorrido desde a resposta). */
    srIsBeforeOpening() {
      const opensAt = toTimestamp(this.srReport.opensAt)
      if (opensAt === null) return false
      const serverNow = toTimestamp(this.srReport.serverNow)
      const now = serverNow === null
        ? Date.now()
        : serverNow + (Date.now() - (this.srReport.serverNowReceivedAt || Date.now()))

      return now < opensAt
    },

    /**
     * 423 APPOINTMENT_REPORT_NOT_OPEN ("houve reunião" antes do horário, pelo relógio do servidor): volta para o
     * "antes do horário" com o horário do servidor. As respostas ficam; o "não houve reunião" continua finalizando.
     */
    srApplyNotOpen(body) {
      const data = body || {}
      if (data.opens_at) this.srReport.opensAt = data.opens_at
      if (data.server_now) this.srReport.serverNow = data.server_now
      this.srReport.serverNowReceivedAt = Date.now()
      this.srClearTimers()
      this.srReport.unlocked = false
      this.srReport.releasedFx = false
      this.srReport.early = true
      this.srReport.errorMessage = ''
      this.srReport.status = SR_REPORT_STATUS.EDITING
    },

    /** O banner de horário chegou a zero: o caminho do "houve reunião" libera. */
    srOnReleased(payload) {
      if (!this.srReport.early) return
      this.srReport.early = false
      if (payload && payload.immediate) {
        this.srReport.unlocked = false
        if (this.srReport.draftDirty) this.srScheduleDraftSave()
        return
      }
      this.srReport.unlocked = true
      this.srReport.releasedFx = true
      clearTimeout(this.$_srReleaseTimer)
      this.$_srReleaseTimer = setTimeout(() => {
        this.srReport.releasedFx = false
      }, RELEASE_FX_MS)
      if (this.srReport.draftDirty) this.srScheduleDraftSave()
    },

    /**
     * 423 APPOINTMENT_REPORT_PENDING com este compromisso FORA dos `pending_ids`: saiu das pendências sem report
     * (cancelado, excluído ou transferido por outra pessoa) enquanto outras continuam — o servidor recusa rascunho e
     * finalize dele com a trava. O formulário mostra o aviso e avisa a tela (a central tira o item da lista).
     * `false` = ainda pendente (o interceptor já cuidou do redirecionamento, quando precisa).
     */
    srApplyPendingRejection(error) {
      const ids = responseData(error).pending_ids
      const pendingIds = (Array.isArray(ids) ? ids : []).map(Number)
      const id = this.srReport.appointmentId
      if (!id || pendingIds.includes(id)) return false
      this.srClearTimers()
      this.srReport.draftDirty = false
      this.srSetUnavailable(REPORT_ERROR_CODES.NOT_REPORTABLE, APP_COPY.notReportable)
      const payload = { appointmentId: id, pendingIds }
      this.srCallHook('onStandardReportPendingRejected', payload)
      this.$emit('pending-rejected', payload)

      return true
    },

    /** 422 de dono/tipo/status: a tela mostra a mensagem do servidor, sem formulário. */
    srApplyUnavailable(error) {
      const { code } = responseData(error)
      if (responseStatus(error) !== 422 || !UNAVAILABLE_CODES.includes(code)) return false
      this.srSetUnavailable(code, getApiErrorMessage(error, ''))
      return true
    },

    srSetUnavailable(code, message) {
      this.srClearTimers()
      this.srCancelOverlapCheck()
      this.srReport.status = SR_REPORT_STATUS.UNAVAILABLE
      this.srReport.unavailableCode = code
      this.srReport.errorMessage = message || ''
    },

    // ---------------------------------------------------------------------------------------------------------
    // Respostas
    // ---------------------------------------------------------------------------------------------------------

    /**
     * Grava uma resposta e agenda o autosave. Efeitos de cada campo (os da sidebar):
     *  - "Houve reunião?" limpa o resultado, o motivo e o "Deseja remarcar?";
     *  - "Fechou negócio?" / "Foi aprovado?" limpa o motivo;
     *  - "Deseja remarcar? Sim" traz o formato atual do compromisso;
     *  - "Data de início" leva o término para 1 h depois;
     *  - a nota do especialista troca os destaques disponíveis (e limpa os escolhidos).
     */
    srSetAnswer(key, value) {
      if (!this.srIsEditable) return false
      // Antes do horário, com o caminho em prévia, só "Houve reunião?" responde.
      if (this.srHeldLocked && key !== F.HAD_MEETING) return false
      const { answers } = this.srReport
      if (!Object.prototype.hasOwnProperty.call(answers, key)) return false
      let next = value === undefined ? null : value
      if (key === F.HIGHLIGHTS) next = Array.isArray(next) ? next.slice(0, MAX_QUALITATIVE_HIGHLIGHTS) : []
      if (key === F.SPECIALIST_ID || key === F.SPECIALIST_SCORE) {
        const number = Number(next)
        next = next !== null && next !== '' && Number.isInteger(number) && number > 0 ? number : null
      }
      const previous = answers[key]
      if (sameValue(previous, next)) return false

      this.srReport.nowTick = Date.now()
      answers[key] = next
      const cleared = []
      switch (key) {
        case F.HAD_MEETING:
          cleared.push(F.CLOSED_DEAL, F.HAS_APPROVAL, F.WHY, F.RESCHEDULE)
          this.srPlayRowsFx()
          break
        case F.CLOSED_DEAL:
        case F.HAS_APPROVAL:
          cleared.push(F.WHY)
          break
        case F.RESCHEDULE:
          if (next === true && !answers[F.MEETING_FORMAT]) {
            const appointment = this.srReport.appointment || {}
            answers[F.MEETING_FORMAT] = appointment.meeting_format || null
          }
          break
        case F.MEETING_START:
          answers[F.MEETING_END] = defaultMeetingEnd(next)
          break
        case F.SPECIALIST_SCORE:
          answers[F.HIGHLIGHTS] = []
          if (next) this.srLoadHighlights(next)
          else this.srReport.highlightOptions = []
          break
        default:
      }
      cleared.forEach(field => {
        answers[field] = null
        if (this.srReport.serverErrors[field]) this.$delete(this.srReport.serverErrors, field)
      })
      if (key === F.MEETING_START || key === F.MEETING_END || key === F.RESCHEDULE || key === F.HAD_MEETING) {
        this.srScheduleOverlapCheck()
      }

      // O link/endereço só conta como "mexido" ao sair do campo (`srTouch`): a URL inválida não pisca enquanto o PN digita.
      if (key !== F.ROOM_LINK) this.$set(this.srReport.touched, key, true)
      if (this.srReport.serverErrors[key]) this.$delete(this.srReport.serverErrors, key)
      // Mudou a resposta depois de uma falha: sai do estado de erro (o CTA volta a "Finalizar report").
      if (this.srReport.status === SR_REPORT_STATUS.ERROR) {
        this.srReport.status = SR_REPORT_STATUS.EDITING
        this.srReport.errorMessage = ''
      }
      this.srScheduleDraftSave()
      return true
    },

    /** Saiu do campo (link/endereço): libera a mensagem de valor inválido antes de tentar finalizar. */
    srTouch(key) {
      if (!this.srReport.touched[key]) this.$set(this.srReport.touched, key, true)
    },

    srItem(key) {
      return this.srItems.find(item => item.key === key) || null
    },

    srIsItemActive(key) {
      return !!this.srItem(key)
    },

    srIsItemMissing(key) {
      const item = this.srItem(key)
      return !!item && !item.answered
    },

    /** Mensagem do item: a do servidor (422) ou a da matriz. */
    srItemMessage(key) {
      if (this.srReport.serverErrors[key]) return this.srReport.serverErrors[key]
      const item = this.srItem(key)
      return item ? item.message : ''
    },

    /**
     * Mostra o erro do item: depois de tentar finalizar (ou com erro do servidor). Valor INVÁLIDO já preenchido
     * (URL, término antes do início, horário ocupado) aparece assim que o PN mexe no campo — a sidebar também
     * mostrava o conflito de horário na hora.
     */
    srShowItemError(key) {
      if (!this.srIsEditable && !this.srIsFinalizing) return false
      if (this.srReport.serverErrors[key]) return true
      if (!this.srIsItemMissing(key)) return false
      if (this.srReport.attempted) return true
      const value = this.srReport.answers[key]
      const filled = value !== null && value !== '' && !(Array.isArray(value) && !value.length)
      if (key === F.MEETING_START && this.srReport.overlapping) return true
      return RESCHEDULE_FIELDS.includes(key) && filled && !!this.srReport.touched[key]
    },

    /** O select/campos liberados pela pergunta estão visíveis. */
    srFollowUpVisible(key) {
      if (this.srHeldLocked && key !== F.HAD_MEETING) return false
      const { answers } = this.srReport
      switch (key) {
        case F.HAD_MEETING: return answers[F.HAD_MEETING] === false
        case F.CLOSED_DEAL:
        case F.HAS_APPROVAL: return key === this.srResultKey && answers[F.HAD_MEETING] === true && answers[key] === false
        case F.RESCHEDULE: return this.srReschedules
        case F.HAD_SUPPORT: return this.srContext.asksSupport && answers[F.HAD_SUPPORT] === true
        default: return false
      }
    },

    /** Glyph de status da pergunta: respondida E o que ela liberou também respondido. */
    srIsQuestionComplete(key) {
      const value = this.srReport.answers[key]
      if (value !== true && value !== false) return false
      if (!this.srFollowUpVisible(key)) return true
      let dependents = []
      if (key === F.RESCHEDULE) dependents = RESCHEDULE_FIELDS
      else if (key === F.HAD_SUPPORT) dependents = SPECIALIST_FIELDS
      else dependents = [F.WHY]

      return !dependents.some(field => this.srIsItemMissing(field))
    },

    /** Nome do `ref` do componente/campo do item (usado pelo "ir para a resposta que falta"). */
    srItemRef(key) {
      return `sr-item-${key}`
    },

    // Ordem visual das linhas Sim | Não (atraso escalonado da entrada no horário liberado).
    srQuestionIndex(key) {
      const order = [F.HAD_MEETING, this.srResultKey, F.RESCHEDULE, F.HAD_SUPPORT]
      return Math.max(0, order.indexOf(key))
    },

    srYesNoProps(key) {
      return {
        value: this.srReport.answers[key],
        legend: standardQuestionLabel(key, this.srContext),
        invalid: this.srShowItemError(key),
        errorMessage: this.srItemMessage(key),
        complete: this.srIsQuestionComplete(key),
        readonly: !this.srIsEditable,
        locked: this.srHeldLocked && key !== F.HAD_MEETING,
        followUpVisible: this.srFollowUpVisible(key),
        compact: this.srCompact,
        enterFx: this.srReport.releasedFx,
        rowEnterFx: this.srReport.rowsFx && key !== F.HAD_MEETING && key !== F.HAD_SUPPORT,
        enterDelay: this.srQuestionIndex(key) * ENTER_STAGGER_MS,
        idPrefix: `${this.srUid}-${key}`,
      }
    },

    srSelectProps(key, config) {
      return {
        value: this.srReport.answers[key] === null || this.srReport.answers[key] === undefined
          ? null
          : String(this.srReport.answers[key]),
        reasonKey: '',
        invalid: this.srShowItemError(key),
        errorMessage: this.srItemMessage(key),
        readonly: !this.srIsEditable,
        maxWidth: this.srCompact ? '600px' : '520px',
        idPrefix: `${this.srUid}-${key}`,
        ...config,
      }
    },

    srWhyProps() {
      return this.srSelectProps(F.WHY, { label: STANDARD_COPY.why, options: this.srWhyOptions })
    },

    srMeetingFormatProps() {
      return this.srSelectProps(F.MEETING_FORMAT, {
        label: STANDARD_COPY.meetingFormat,
        options: this.srMeetingFormatOptions,
        placeholder: STANDARD_COPY.meetingFormatPlaceholder,
        maxWidth: 'none',
      })
    },

    srSpecialistProps() {
      return this.srSelectProps(F.SPECIALIST_ID, {
        label: STANDARD_COPY.specialist,
        options: this.srSpecialistOptions,
        // Mesmo placeholder do "PN Especialista" do Novo compromisso (Figma 1:6).
        placeholder: FIGMA_COPY.specialistPlaceholder,
      })
    },

    srRatingProps() {
      return {
        score: this.srReport.answers[F.SPECIALIST_SCORE],
        highlights: this.srReport.answers[F.HIGHLIGHTS],
        options: this.srReport.highlightOptions,
        loading: this.srReport.highlightsLoading,
        max: MAX_QUALITATIVE_HIGHLIGHTS,
        scoreInvalid: this.srShowItemError(F.SPECIALIST_SCORE),
        scoreError: this.srItemMessage(F.SPECIALIST_SCORE),
        highlightsInvalid: this.srShowItemError(F.HIGHLIGHTS),
        highlightsError: this.srItemMessage(F.HIGHLIGHTS),
        readonly: !this.srIsEditable,
        idPrefix: `${this.srUid}-rating`,
      }
    },

    srListeners(key) {
      return {
        input: value => this.srSetAnswer(key, value),
      }
    },

    srRatingListeners() {
      return {
        'update:score': value => this.srSetAnswer(F.SPECIALIST_SCORE, value),
        'update:highlights': value => this.srSetAnswer(F.HIGHLIGHTS, value),
      }
    },

    srSpecialistName(id) {
      if (!id) return ''
      const option = this.srSpecialistOptions.find(item => item.value === String(id))
      return option && option.label !== String(id) ? option.label : ''
    },

    srPlayRowsFx() {
      this.srReport.rowsFx = true
      clearTimeout(this.$_srRowsFxTimer)
      this.$_srRowsFxTimer = setTimeout(() => {
        this.$_srRowsFxTimer = null
        this.srReport.rowsFx = false
      }, ROWS_FX_MS)
    },

    /** Leva à primeira resposta que falta (ou a `preferredKey`), mostrando as mensagens de obrigatório. */
    srJumpToMissing(preferredKey) {
      if (!this.srIsEditable) return
      this.srReport.attempted = true
      const preferred = typeof preferredKey === 'string' && this.srIsItemActive(preferredKey) ? preferredKey : ''
      const first = this.srMissingItems[0]
      const key = preferred || (first && first.key)
      if (!key) return
      this.$nextTick(() => this.srFocusItem(key))
    },

    /**
     * Foco no campo do item. Os destaques ficam no mesmo componente da nota (`focusHighlights`); o flatpickr e o
     * input de link não têm `focus()` de componente — vale o elemento.
     */
    srFocusItem(key) {
      const refKey = key === F.HIGHLIGHTS ? F.SPECIALIST_SCORE : key
      let target = this.$refs[this.srItemRef(refKey)]
      if (Array.isArray(target)) [target] = target
      if (!target) return
      const element = target.$el || target
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
      }
      if (key === F.HIGHLIGHTS && typeof target.focusHighlights === 'function') {
        target.focusHighlights({ preventScroll: true })
      } else if (typeof target.focus === 'function') {
        target.focus({ preventScroll: true })
      } else if (element && typeof element.focus === 'function') {
        element.focus({ preventScroll: true })
      }
    },

    // ---------------------------------------------------------------------------------------------------------
    // Reagendamento — checagem de horário ocupado (a da sidebar) e o POST /api/appointments
    // ---------------------------------------------------------------------------------------------------------
    srCancelOverlapCheck() {
      clearTimeout(this.$_srOverlapTimer)
      this.$_srOverlapTimer = null
      this.$_srOverlapSeq += 1
      this.$_srOverlapRequest = null
      this.srReport.overlapping = false
      this.srReport.overlapChecking = false
    },

    /** 200ms depois de mudar o horário, pergunta ao servidor se ele está livre (só com início < término). */
    srScheduleOverlapCheck() {
      this.srCancelOverlapCheck()
      if (!this.srReschedules || !hasValidRescheduleRange(this.srReport.answers)) return
      this.srReport.overlapChecking = true
      this.$_srOverlapTimer = setTimeout(() => {
        this.$_srOverlapTimer = null
        this.srCheckOverlap()
      }, OVERLAP_CHECK_DELAY_MS)
    },

    /** Resolve com `true` quando o horário está ocupado (405). Rede/validação/5xx não bloqueiam: o servidor decide. */
    srCheckOverlap() {
      this.$_srOverlapSeq += 1
      const sequence = this.$_srOverlapSeq
      const { answers, appointment } = this.srReport
      const consultant = appointment && appointment.consultant
      this.srReport.overlapChecking = true
      const request = this.$store.dispatch(appointmentTypes.CHECK_FOR_OVERLAPPING_APPOINTMENTS, {
        appointment_start: stringToDateTime(answers[F.MEETING_START]),
        appointment_end: stringToDateTime(answers[F.MEETING_END]),
        consultant_id: consultant && consultant.id,
      })
        .then(() => false)
        .catch(error => responseStatus(error) === OVERLAP_STATUS)
        .then(overlapping => {
          if (sequence === this.$_srOverlapSeq && !this.$_srDestroyed) {
            this.srReport.overlapping = overlapping
            this.srReport.overlapChecking = false
            this.$_srOverlapRequest = null
          }
          return overlapping
        })
      this.$_srOverlapRequest = request

      return request
    },

    /** Antes de finalizar: a checagem pendente (debounce ou em andamento) precisa terminar. */
    srAwaitOverlapCheck() {
      if (!this.srReschedules || !this.srReport.overlapChecking) return Promise.resolve()
      if (this.$_srOverlapTimer) {
        clearTimeout(this.$_srOverlapTimer)
        this.$_srOverlapTimer = null
        return this.srCheckOverlap().then(() => undefined)
      }
      return (this.$_srOverlapRequest || Promise.resolve()).then(() => undefined)
    },

    /**
     * "Deseja remarcar a reunião? Sim": o novo compromisso sai num `POST /api/appointments` com os mesmos campos da
     * sidebar. Roda DEPOIS do report gravado (a trava deixa passar o reagendamento de um "não houve reunião"
     * recém-gravado) e uma vez por formulário — um "Tentar novamente" do report não cria outro compromisso. Nunca
     * rejeita: a falha fica na tela de sucesso, com "Tentar novamente" só do reagendamento.
     */
    srReschedule() {
      const id = this.srReport.appointmentId
      if (this.$_srRescheduleDone || this.srReport.reschedule.status === 'saving') return Promise.resolve(false)
      const payload = buildReschedulePayload(this.srReport.answers, this.srReport.appointment, stringToDateTime)
      this.srReport.reschedule = { status: 'saving', message: '' }

      return this.$store.dispatch(appointmentTypes.STORE_APPOINTMENT, payload)
        .then(response => {
          if (this.$_srDestroyed || id !== this.srReport.appointmentId) return false
          this.$_srRescheduleDone = true
          this.srReport.reschedule = { status: 'done', message: '' }
          this.srToast({ text: APP_COPY.appointmentCreated, icon: 'CoffeeIcon', variant: 'success' })
          this.$emit('rescheduled', { appointmentId: id, response: (response && response.data) || null })
          return true
        })
        .catch(error => {
          if (this.$_srDestroyed || id !== this.srReport.appointmentId) return false
          const message = rescheduleErrorMessage(error)
          this.srReport.reschedule = { status: 'error', message }
          if (!isReportPendingError(error)) this.srToast({ text: message, variant: 'danger' })
          return false
        })
    },

    srRetryReschedule() {
      if (!this.srIsSaved || this.$_srRescheduleDone) return Promise.resolve(false)
      return this.srReschedule()
    },

    // ---------------------------------------------------------------------------------------------------------
    // Destaques do especialista (a mesma consulta do componente de estrelas da sidebar)
    // ---------------------------------------------------------------------------------------------------------
    srLoadHighlights(score, options) {
      const keepSelection = !!(options && options.keepSelection)
      this.$_srHighlightsSeq += 1
      const sequence = this.$_srHighlightsSeq
      const apply = names => {
        this.srReport.highlightOptions = names
        // Rascunho restaurado: fica só o que ainda existe para a nota (o resto não aparece e não pode ir no report).
        if (keepSelection && names.length) {
          const selected = this.srReport.answers[F.HIGHLIGHTS]
          this.srReport.answers[F.HIGHLIGHTS] = selected.filter(name => names.includes(name))
        }
      }
      const cached = this.$_srHighlightsCache[score]
      if (cached) {
        apply(cached)
        this.srReport.highlightsLoading = false
        return Promise.resolve(cached)
      }

      this.srReport.highlightOptions = []
      this.srReport.highlightsLoading = true
      return this.$store.dispatch(appointmentTypes.GET_APPOINTMENT_QUALITATIVE_HIGHLIGHTS, `${score}.0`)
        .then(resp => {
          const rows = resp && Array.isArray(resp.data) ? resp.data : []
          const names = rows
            .map(row => (row && typeof row.qualitative_highlight_name === 'string' ? row.qualitative_highlight_name : ''))
            .filter(Boolean)
          this.$_srHighlightsCache[score] = names
          if (sequence === this.$_srHighlightsSeq && !this.$_srDestroyed) apply(names)
          return names
        })
        .catch(error => {
          if (sequence === this.$_srHighlightsSeq && !this.$_srDestroyed && !isReportPendingError(error)) {
            this.srToast({ text: STANDARD_COPY.highlightsError, variant: 'danger' })
          }
          return []
        })
        .finally(() => {
          if (sequence === this.$_srHighlightsSeq && !this.$_srDestroyed) this.srReport.highlightsLoading = false
        })
    },

    // ---------------------------------------------------------------------------------------------------------
    // Rascunho (autosave) — o mesmo protocolo do Momento X, com `form_version = 1`
    // ---------------------------------------------------------------------------------------------------------
    srScheduleDraftSave() {
      if (this.srReport.draftDisabled) return
      this.srReport.draftDirty = true
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = setTimeout(() => {
        this.$_srDraftTimer = null
        this.srSaveDraft()
      }, DRAFT_SAVE_DEBOUNCE_MS)
    },

    srDraftPayload(version) {
      const { answers } = this.srReport
      return {
        id: this.srReport.appointmentId,
        answers: { ...answers, [F.HIGHLIGHTS]: [...answers[F.HIGHLIGHTS]] },
        version,
        formVersion: STANDARD_FORM_VERSION,
      }
    },

    /**
     * PUT do rascunho com TODAS as respostas (inclusive dos campos escondidos, para restaurar). Um PUT por vez: o
     * próximo espera a `version` devolvida pelo anterior (senão o servidor acusaria conflito com a própria aba).
     */
    srSaveDraft() {
      if (!this.srReport.draftDirty || !this.srIsEditable || this.srReport.draftDisabled) return Promise.resolve(null)
      if (this.$_srDraftRequest) {
        this.$_srDraftQueued = true
        return this.$_srDraftRequest
      }
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = null
      clearTimeout(this.$_srDraftRetryTimer)
      this.$_srDraftRetryTimer = null

      const id = this.srReport.appointmentId
      this.srReport.draftDirty = false
      const request = this.$store.dispatch(appointmentTypes.SAVE_REPORT_DRAFT, this.srDraftPayload(this.srReport.draftVersion))
        .then(resp => {
          const draft = resp && resp.data && resp.data.draft
          if (!draft) throw new Error('Resposta vazia ao salvar o rascunho.')
          if (id === this.srReport.appointmentId) {
            // A versão vale mesmo se a tela já estiver finalizando: se o finalize falhar, o próximo PUT precisa dela.
            if (Number.isInteger(draft.version)) this.srReport.draftVersion = draft.version
            this.srReport.draftSavedAt = draft.updated_at || localClock()
            this.srReport.draftSaveFailed = false
            this.$_srDraftRetryDelay = DRAFT_RETRY_BASE_MS
            this.$emit('draft-saved', { appointmentId: id, savedAt: this.srReport.draftSavedAt })
          }
          return draft
        })
        .catch(error => {
          if (this.$_srDestroyed || id !== this.srReport.appointmentId) return null
          if (this.srIsFinalizing) {
            this.srReport.draftDirty = true
            return null
          }
          this.srHandleDraftError(error)
          return null
        })
        .finally(() => {
          if (this.$_srDraftRequest === request) this.$_srDraftRequest = null
          if (this.$_srDraftQueued) {
            this.$_srDraftQueued = false
            if (!this.$_srDestroyed && this.srReport.draftDirty) this.srSaveDraft()
          }
        })
      this.$_srDraftRequest = request

      return request
    },

    /**
     * Salva JÁ a resposta ainda não enviada (sem debounce), encadeada no PUT em andamento para usar a versão que ele
     * devolver. Usado ao sair da tela e ao trocar de compromisso. Nunca rejeita.
     */
    srFlushDraft() {
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = null
      const {
        appointmentId: id,
        draftDirty,
        draftVersion,
        draftDisabled,
      } = this.srReport
      if (!id || !draftDirty || draftDisabled || !this.srIsEditable) return Promise.resolve(null)

      const payload = this.srDraftPayload(draftVersion)
      const previous = this.$_srDraftRequest || Promise.resolve(null)
      this.srReport.draftDirty = false
      this.$_srDraftQueued = false

      return previous
        .then(draft => this.$store.dispatch(appointmentTypes.SAVE_REPORT_DRAFT, {
          ...payload,
          version: draft && Number.isInteger(draft.version) ? draft.version : draftVersion,
        }))
        .catch(() => null)
    },

    srHandleDraftError(error) {
      if (!this.srIsEditable) return
      if (isReportPendingError(error)) {
        this.srApplyPendingRejection(error)
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.DRAFT_CONFLICT)) {
        this.srApplyDraftConflict(error)
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.ALREADY_REPORTED)) {
        this.srHandleAlreadyReported()
        return
      }
      if (isReportNotOpenError(error)) {
        this.srReport.draftDirty = true
        this.srApplyNotOpen(responseData(error))
        return
      }
      // 422 de regra no rascunho (NOT_REPORTABLE / NOT_OWNER): o autosave desliga em silêncio e o formulário segue —
      // quem decide de verdade é o finalize, que mostra a recusa se ela continuar valendo.
      const { code } = responseData(error)
      if (responseStatus(error) === 422 && UNAVAILABLE_CODES.includes(code)) {
        this.srReport.draftDisabled = true
        this.srReport.draftDirty = false
        clearTimeout(this.$_srDraftTimer)
        this.$_srDraftTimer = null
        return
      }

      // Rede / 5xx / validação: as respostas continuam na tela; tenta de novo com espera crescente.
      this.srReport.draftDirty = true
      this.srReport.draftSaveFailed = true
      if (this.$_srDestroyed) return
      clearTimeout(this.$_srDraftRetryTimer)
      const delay = this.$_srDraftRetryDelay
      this.$_srDraftRetryDelay = Math.min(delay * 2, DRAFT_RETRY_MAX_MS)
      this.$_srDraftRetryTimer = setTimeout(() => {
        this.$_srDraftRetryTimer = null
        this.srSaveDraft()
      }, delay)
    },

    /** 409 APPOINTMENT_REPORT_DRAFT_CONFLICT: outra aba salvou antes — carrega as respostas mais recentes. */
    srApplyDraftConflict(error) {
      const { draft } = responseData(error)
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = null
      if (draft) {
        this.srApplyDraft(draft)
        this.srReport.draftSavedAt = draft.updated_at || this.srReport.draftSavedAt
        this.srCancelOverlapCheck()
        this.srLoadSideData()
      } else {
        this.srLoad()
      }
      this.srToast({ text: getApiErrorMessage(error, ''), icon: 'RefreshCwIcon', variant: 'warning' })
    },

    /** Descarta o rascunho aberto no servidor (DELETE idempotente: só apaga rascunho ainda não finalizado). */
    srDiscardDraft() {
      const id = this.srReport.appointmentId
      if (!id) return Promise.resolve(null)
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = null

      return this.$store.dispatch(appointmentTypes.DISCARD_REPORT_DRAFT, id).catch(() => null)
    },

    // ---------------------------------------------------------------------------------------------------------
    // Finalizar
    // ---------------------------------------------------------------------------------------------------------

    /**
     * Finaliza o report. Idempotente: um clique durante o envio é ignorado e todas as tentativas usam a mesma
     * `Idempotency-Key`. Incompleto (ou com horário ocupado) → leva à primeira resposta que falta. Resolve com
     * `{ report, replayed, pending_count }` no sucesso, ou `null`.
     */
    srFinalize() {
      if (this.$_srFinalizing || !this.srIsEditable) return Promise.resolve(null)
      // "Houve reunião? Sim" antes do horário: a barra mostra que o report libera no horário (o servidor recusaria).
      if (this.srFinalizeLocked) return Promise.resolve(null)
      // O horário do reagendamento pode ter passado enquanto o PN preenchia: confere com o "agora" de verdade.
      // O report seria gravado e só o `POST /api/appointments` do reagendamento falharia, sem como trocar a data.
      this.srReport.nowTick = Date.now()
      if (!this.srIsComplete && !this.srReport.overlapChecking) {
        this.srJumpToMissing()
        return Promise.resolve(null)
      }

      // Marca própria deste envio: um envio antigo (de antes de `srInit`) não libera a trava de clique do atual.
      const attempt = {}
      this.$_srFinalizing = attempt
      const id = this.srReport.appointmentId
      const release = () => {
        if (this.$_srFinalizing === attempt) this.$_srFinalizing = false
      }

      // A checagem de horário do reagendamento termina antes (enquanto ela roda, o formulário conta como completo).
      return this.srAwaitOverlapCheck().then(() => {
        if (this.$_srDestroyed || id !== this.srReport.appointmentId || !this.srIsEditable) {
          release()
          return null
        }
        if (!this.srIsComplete) {
          release()
          this.srJumpToMissing()
          return null
        }
        return this.srSubmit(id, attempt)
      })
    },

    srSubmit(id, attempt) {
      clearTimeout(this.$_srDraftTimer)
      this.$_srDraftTimer = null
      clearTimeout(this.$_srDraftRetryTimer)
      this.$_srDraftRetryTimer = null
      if (!this.$_srIdempotencyKey) this.$_srIdempotencyKey = newIdempotencyKey()

      const startedAt = Date.now()
      const minBusyMs = this.srFinalizeMinBusyMs
      const holdBusy = () => wait(minBusyMs - (Date.now() - startedAt))
      const withReschedule = this.srReschedules
      this.srReport.status = SR_REPORT_STATUS.FINALIZING
      this.srReport.errorMessage = ''
      this.srReport.announce = ''

      return this.$store.dispatch(appointmentTypes.FINALIZE_STANDARD_REPORT, {
        id,
        payload: this.srFinalizePayload,
        idempotencyKey: this.$_srIdempotencyKey,
      })
        .then(
          resp => (withReschedule ? this.srReschedule() : Promise.resolve(false))
            .then(() => holdBusy())
            .then(() => this.srApplyFinalized(id, resp)),
          error => holdBusy().then(() => {
            this.srHandleFinalizeError(id, error)
            if (this.srIsEditable && this.srReport.draftDirty) this.srScheduleDraftSave()
            return null
          }),
        )
        .finally(() => {
          if (this.$_srFinalizing === attempt) this.$_srFinalizing = false
        })
    },

    srApplyFinalized(id, resp) {
      if (this.$_srDestroyed || id !== this.srReport.appointmentId) return null
      const data = (resp && resp.data) || {}
      const pendingCount = Number(data.pending_count)
      this.srClearTimers()
      this.srCancelOverlapCheck()
      this.srReport.report = data.report || null
      this.srReport.replayed = !!data.replayed
      this.srReport.pendingCount = Number.isInteger(pendingCount) && pendingCount >= 0 ? pendingCount : null
      this.srReport.draftDirty = false
      this.srReport.status = SR_REPORT_STATUS.SAVED
      this.srReport.announce = NEW_COPY.reportSaved
      // O servidor já fecha o rascunho na finalização; o DELETE (que só apaga rascunho ABERTO) garante que nenhum
      // rascunho em aberto sobre deste compromisso — ex.: um PUT que o servidor ainda não tinha visto.
      this.srDiscardDraft()
      if (this.srReport.report && this.srReport.report.consultant_specialist_id) this.srEnsureSpecialists()

      const payload = {
        appointmentId: id,
        eventType: this.srContext.eventType,
        report: this.srReport.report,
        replayed: this.srReport.replayed,
        pendingCount: this.srReport.pendingCount,
        closedDeal: this.srClosedDeal,
        hasApproval: this.srHasApproval,
        offersContract: this.srOffersContract,
        offersIndividualInterview: this.srOffersIndividualInterview,
        rescheduled: this.srReport.reschedule.status === 'done',
        // Report salvo, mas o reagendamento falhou. A central desmonta este formulário compacto ao receber o
        // `reported` (a confirmação é dela), e com ele iriam as datas e o link escolhidos: o corpo do
        // `POST /api/appointments` vai junto para ela oferecer o "Tentar novamente" só do reagendamento, como a página.
        reschedule: this.srReport.reschedule.status === 'error'
          ? {
            status: 'error',
            message: this.srReport.reschedule.message,
            payload: buildReschedulePayload(this.srReport.answers, this.srReport.appointment, stringToDateTime),
          }
          : null,
        // Para a central montar o "Resumo do report" com as perguntas de sempre (o formulário compacto não mostra
        // a confirmação): as respostas que valeram (as locais trazem os destaques do especialista, que o resumo do
        // servidor não traz) e o contexto do compromisso (recrutamento, Entrevista, pergunta do especialista).
        answers: { ...this.srSavedAnswers, [F.HIGHLIGHTS]: [...(this.srSavedAnswers[F.HIGHLIGHTS] || [])] },
        context: { ...this.srContext },
      }
      this.srCallHook('onStandardReportFinalized', payload)
      this.$emit('reported', payload)

      return data
    },

    srHandleFinalizeError(id, error) {
      if (this.$_srDestroyed || id !== this.srReport.appointmentId) return
      if (isReportPendingError(error)) {
        if (!this.srApplyPendingRejection(error)) this.srReport.status = SR_REPORT_STATUS.EDITING
        return
      }
      if (isReportNotOpenError(error)) {
        this.srApplyNotOpen(responseData(error))
        return
      }
      if (hasApiErrorCode(error, REPORT_ERROR_CODES.ALREADY_REPORTED)) {
        this.srHandleAlreadyReported()
        return
      }
      if (this.srApplyUnavailable(error)) return

      if (responseStatus(error) === 422) {
        // Validação do FormRequest (a matriz do front é a mesma, então é raro): marca os campos e volta a editar.
        const errors = responseData(error).errors || {}
        const serverErrors = {}
        Object.keys(errors).forEach(name => {
          const field = name.split('.')[0]
          if (!SERVER_FIELDS.includes(field) || serverErrors[field]) return
          const messages = errors[name]
          serverErrors[field] = Array.isArray(messages) ? messages[0] : String(messages || '')
        })
        this.srReport.serverErrors = serverErrors
        this.srReport.status = SR_REPORT_STATUS.EDITING
        this.srToast(apiErrorToast(error, NEW_COPY.finalizeError))
        this.srJumpToMissing(Object.keys(serverErrors)[0])
        return
      }

      // Rede, timeout, 5xx: nunca mostra sucesso; respostas mantidas; "Tentar novamente" reusa a mesma chave.
      this.srReport.status = SR_REPORT_STATUS.ERROR
      this.srReport.errorMessage = NEW_COPY.finalizeError
    },

    /** 409 APPOINTMENT_ALREADY_REPORTED: mostra o report que já existe (somente leitura). */
    srHandleAlreadyReported() {
      const id = this.srReport.appointmentId
      this.srClearTimers()
      this.srCancelOverlapCheck()
      this.srReport.draftDirty = false
      this.srReport.status = SR_REPORT_STATUS.ALREADY_REPORTED
      let emitted = false

      return this.$store.dispatch(appointmentTypes.GET_REPORT_DRAFT, id)
        .then(resp => {
          if (this.$_srDestroyed || id !== this.srReport.appointmentId) return
          const body = (resp && resp.data) || {}
          if (body.appointment) this.srReport.appointment = body.appointment
          if (body.report) {
            emitted = true
            this.srApplyExistingReport(body.report)
          }
        })
        .catch(() => {})
        .then(() => {
          // Sem o resumo, a tela continua em "já reportado" — e a central ainda precisa saber (senão espera para sempre).
          if (emitted || this.$_srDestroyed || id !== this.srReport.appointmentId) return
          this.srCallHook('onStandardReportAlreadyReported', null)
          this.$emit('already-reported', null)
        })
    },

    // ---------------------------------------------------------------------------------------------------------
    // Depois de salvar: os próximos passos da sidebar
    // ---------------------------------------------------------------------------------------------------------

    /** "Reportar agora": o cadastro do contrato do negócio fechado (os mesmos params do `onContractReport`). */
    srOpenContract() {
      return this.$router.push({
        name: CONTRACT_SAVE_ROUTE,
        params: { blocked: false, appointment: this.srReport.appointmentId },
      }).catch(() => {})
    },

    /** "Responder agora": o questionário da entrevista individual do candidato (`openIndividualInterview`). */
    srOpenIndividualInterview() {
      const { appointment } = this.srReport
      const customerId = (appointment && appointment.customer && appointment.customer.id)
        || (this.$_srHint && this.$_srHint.customer_id)
      if (!customerId) return Promise.resolve()

      return this.$router.push({ name: INDIVIDUAL_INTERVIEW_ROUTE, params: { id: customerId } }).catch(() => {})
    },

    /** "Reportar depois" / "Responder depois". */
    srDismissPostSave() {
      const kind = this.srPostSave
      this.srReport.postSaveDismissed = true
      this.$emit('post-save-dismissed', { appointmentId: this.srReport.appointmentId, kind })
    },

    // ---------------------------------------------------------------------------------------------------------
    // Utilitários
    // ---------------------------------------------------------------------------------------------------------
    srClearTimers() {
      clearTimeout(this.$_srDraftTimer)
      clearTimeout(this.$_srDraftRetryTimer)
      clearTimeout(this.$_srReleaseTimer)
      clearTimeout(this.$_srRowsFxTimer)
      this.$_srDraftTimer = null
      this.$_srDraftRetryTimer = null
      this.$_srReleaseTimer = null
      this.$_srRowsFxTimer = null
      this.srReport.releasedFx = false
      this.srReport.rowsFx = false
    },

    srCallHook(name, payload) {
      if (typeof this[name] === 'function') this[name](payload)
    },

    srToast(props) {
      if (!this.$toast || !props || !props.text) return
      this.$toast({
        component: ToastificationContent,
        props: {
          title: props.variant === 'success' ? 'Sucesso' : 'Oops!',
          icon: 'AlertTriangleIcon',
          ...props,
        },
      })
    },
  },
}
